import { FormikProps } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { useBeforeUnload } from "react-router-dom";
import { Box, Flex } from "@chakra-ui/react";
import BinarySplitView from "@/features/ui/binary-split-view";
import InvoiceFileFormControl from "@/features/invoices/order-confirmation-invoices/invoice-file-form-control";
import useIdentity from "@/features/ui/hooks/use-identity";
import ProformaInvoiceInformationFormControlModal
  from "@/features/invoices/proforma-invoices/proforma-invoice-information-form-control-modal";
import {
  ProformaInvoiceFormFieldsModal
} from "@/features/invoices/proforma-invoices/upload-proforma-invoice-page-modal";
import ProformaInvoiceFigureFormControlModal
  from "@/features/invoices/proforma-invoices/proforma-invoice-figure-form-control-modal";
import { LightPayment } from "@/features/invoices/payment/payment.type";
import InvoicePaymentsPanel from "@/features/invoices/payment/invoice-payments-panel";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import LinkedOrderSheetSetsFormControl from "@/features/invoices/order-confirmation-invoices/linked-order-sheet-sets-form-control";
import { ListAllDenseOrderSheetSetsRequest } from "@/features/order-sheet-sets/hooks/use-list-all-dense-order-sheet-sets";
import LinkedOrderConfirmationInvoicesFormControl from "@/features/invoices/proforma-invoices/linked-order-confirmation-invoices-form-control";
import { ListAllOrderConfirmationInvoicesRequest } from "@/features/invoices/order-confirmation-invoices/hooks/use-list-all-order-confirmation-invoices";
import { remainingDepositOf } from "@/features/invoices/proforma-invoices/cross-trade-fee";

interface ProformaInvoiceFormProps {
  formik: FormikProps<ProformaInvoiceFormFieldsModal>;
  isEditing?: boolean;
  payments?: LightPayment[];
  // 시즌 프로포마 — 오더 대신 OC를 연결하고, 수치 카드에 디파짓을 보여준다
  isSeason?: boolean;
}

export default function ProformaInvoiceFormModal({
  formik,
  isEditing = false,
  payments = [],
  isSeason = false,
}: ProformaInvoiceFormProps) {
  const identity = useIdentity();
  const { tCurrencyString } = useI18nHelper();
  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (formik.isSubmitting || (formik.dirty && isEditing)) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    [formik.isSubmitting, formik.dirty, isEditing]
  );

  useBeforeUnload(handleBeforeUnload);

  useEffect(() => {
    //수정 취소시 기본값으로 돌리기
    if (!formik.isSubmitting && formik.dirty && !isEditing) {
      formik.resetForm();
    }
  }, [isEditing, formik]);

  // 우노 수수료 결제가 완료(COMPLETED)되지 않았으면 "송금 필요" 상태로 보고,
  // 이 경우 우노 수수료 환율을 (전체 잠금 여부와 무관하게) 수정 가능하게 한다.
  const unoFeePayment = payments.find((payment) => payment.type === "UNO_FEE");
  const isUnoFeeRemittanceRequired = unoFeePayment?.status !== "COMPLETED";

  const isCreating = formik.values.id === -1;
  const isDisabled =
    !isCreating &&
    isEditing &&
    (formik.values.payments.some((payment) =>
        payment.sender.id === formik.values.orderedBy?.id && (formik.values.isRemittedToAgency || formik.values.isRemittedToBoutique)
    ) ||
      formik.values.status !== "NORMAL");

  // 연결된 오더 후보 목록 필터(페이지 폼과 동일 조건).
  const listOrderSheetSetsRequest = useMemo(():
    | ListAllDenseOrderSheetSetsRequest
    | undefined => {
    const issuedBy = formik.values.originallyIssuedBy || formik.values.issuedBy;
    if (formik.values.orderedBy && issuedBy && formik.values.issuedThrough) {
      return {
        status__in: ["BOUTIQUE_CONFIRMED"],
        "createdByCompany.id__in": [formik.values.orderedBy.id],
        "orderSheet.toBeSubmitted.id__eq": issuedBy.id,
        "lineSheetSet.createdByCompany.id__eq": formik.values.issuedThrough.id,
      };
    }
    return undefined;
  }, [
    formik.values.orderedBy,
    formik.values.originallyIssuedBy,
    formik.values.issuedBy,
    formik.values.issuedThrough,
  ]);

  // 시즌: 연결할 OC 후보 목록 필터 — 부티크 송금이 끝난 공개 OC. 시즌은 조건에서 뺀다(OC와 프로포마 시즌이 달라도 연결).
  const listOrderConfirmationInvoicesRequest = useMemo(():
    | ListAllOrderConfirmationInvoicesRequest
    | undefined => {
    const issuedBy = formik.values.originallyIssuedBy || formik.values.issuedBy;
    if (
      isSeason &&
      issuedBy &&
      formik.values.orderedBy &&
      formik.values.issuedThrough
    ) {
      return {
        "issuedByCompany.id__eq": issuedBy.id,
        "issuedToCompany.id__eq": formik.values.orderedBy.id,
        "issuedThroughCompany.id__eq": formik.values.issuedThrough.id,
        isRemittedToBoutique__eq: true,
        paidBy__eq: formik.values.paidByAgency ? "AGENCY" : "BUYER",
        isPublic__eq: true,
        status__eq: "NORMAL",
      };
    }
    return undefined;
  }, [
    isSeason,
    formik.values.originallyIssuedBy,
    formik.values.issuedBy,
    formik.values.orderedBy,
    formik.values.issuedThrough,
    formik.values.paidByAgency,
  ]);

  return (
    <Box padding={"24px"} overflow={"hidden"}>
      <BinarySplitView
        left={
          <Flex flexDirection={"column"} gap={"24px"}>
            <ProformaInvoiceInformationFormControlModal
              formik={formik}
              isEditing={isEditing}
              isDisabled={isDisabled}
              isUnoFeeRemittanceRequired={isUnoFeeRemittanceRequired}
            />
            <InvoiceFileFormControl
              formik={formik}
              isEditing={isEditing}
            />
          </Flex>
        }
        right={
          <Flex flexDirection={"column"} gap={"24px"}>
            {isSeason && !(
              identity?.company?.type === "BOUTIQUE" && formik.values.id === -1
            ) && (
              <LinkedOrderConfirmationInvoicesFormControl
                formik={formik}
                isEditing={isEditing}
                request={listOrderConfirmationInvoicesRequest}
                // 디파짓은 발행한 회사의 프로포마에서만 쓴다 — OC 발행사(3자무역사 포함)가 프로포마 발행사와 같은 것만
                optionFilter={(ocInvoice) => ocInvoice.issuedByCompany?.id === formik.values.issuedBy?.id}
                // 새 프로포마는 연결 가능한 OC 가 있으면 첫 번째를 자동으로 고른다
                autoSelectFirst={isCreating}
                isDisabled={isDisabled}
                onSelect={(value) => {
                  formik.setValues((prev) => {
                    // 잔여 디파짓은 OC 헤더 기준. 수정 화면에서 이미 연결돼 있던 OC 는 이 프로포마가 쓴 디파짓이
                    // 되더해진 초기값을 그대로 쓴다(서버가 되돌린 뒤 다시 깎는다).
                    const orderConfirmationInvoices = value.map((ocInvoice) => {
                      const initialOcInvoice = formik.initialValues.orderConfirmationInvoices.find(
                        (initial) => initial.id === ocInvoice.id
                      );
                      return {
                        ...ocInvoice,
                        expectedRemainingDepositAmount:
                          initialOcInvoice?.expectedRemainingDepositAmount ??
                          ocInvoice.expectedRemainingDepositAmount,
                        // 폼은 OC id·잔여 디파짓만 쓴다 — 결제 목록은 형식(Dense)이 달라 비워 둔다
                        payments: [],
                      };
                    });
                    return {
                      ...prev,
                      orderSheetSets: [],
                      orderConfirmationInvoices,
                      proformaInvoiceDetailList: [],
                      expectedRemainingDepositAmount: remainingDepositOf(
                        orderConfirmationInvoices,
                        prev.currency ?? "EUR"
                      ),
                      lineSheetSetType: "SEASON",
                    };
                  });
                }}
              />
            )}
            {!isSeason && !(
              identity?.company?.type === "BOUTIQUE" && formik.values.id === -1
            ) && (
              <LinkedOrderSheetSetsFormControl
                formik={formik}
                isEditing={isEditing}
                request={listOrderSheetSetsRequest}
                nameFunc={(oss) =>
                  `${oss.id} / ${oss.lineSheetSet.name} / ${tCurrencyString(
                    oss.confirmedAmount ?? 0,
                    { type: "SYMBOL", code: formik.values.currency ?? "EUR" }
                  )}`
                }
                isDisabled={
                  isDisabled ||
                  (formik.values.orderConfirmationInvoices &&
                    formik.values.orderConfirmationInvoices.length > 0)
                }
                onSelect={(value) => {
                  formik.setValues((prev) => {
                    return {
                      ...prev,
                      orderSheetSets: value,
                      // 오더 변경 시 총액 초기값을 다시 채우도록 비운다(figure 컨트롤이 확정 금액 합으로 채운다)
                      totalAmount: undefined,
                      proformaInvoiceDetailList: [],
                      lineSheetSetType:
                        value.length > 0 ? "STOCK" : prev.lineSheetSetType,
                    };
                  });
                }}
              />
            )}
            {!(
              identity?.company?.type === "BOUTIQUE" && formik.values.id === -1
            ) && (
              <ProformaInvoiceFigureFormControlModal
                formik={formik}
                isEditing={isEditing}
                isDisabled={isDisabled}
                isSeason={isSeason}
              />
            )}
            <InvoicePaymentsPanel payments={payments} />
          </Flex>
        }
        minLeftWidth={400}
        minRightWidth={400}
      />
    </Box>
  );
}
